<script setup lang="ts">
import { NumericEvaluationParameterDto } from '@/api/generator';
import NumberField from '@/components/forms/NumberField/NumberField.vue';
import {
  getFormattingOptionsFromEvaluationParameter,
  NumberDisplayOptions,
} from '@/utils/numberUtils';
import { I18nKey } from '@/utils/typings';
import { useTranslation } from 'i18next-vue';
import { computed, ref } from 'vue';

const { t } = useTranslation();

const props = defineProps<{
  evaluation: NumericEvaluationParameterDto;
  value?: number;
  disabled?: boolean;
  formatValue?: NumberDisplayOptions;
}>();

const modelValue = ref<number | undefined>(props.value);

const emits = defineEmits<{
  input: [result: number];
}>();

const format = computed<NumberDisplayOptions | undefined>(() => {
  if (props.formatValue) {
    return props.formatValue;
  }
  return getFormattingOptionsFromEvaluationParameter(props.evaluation);
});

const errorMessage = computed(() => {
  const [inputValue, minValue, maxValue] = [
    modelValue.value,
    props.evaluation.min ?? undefined,
    props.evaluation.max ?? undefined,
  ];

  if (inputValue === undefined) {
    return undefined;
  }

  if (isNaN(inputValue)) {
    const valueType = props.evaluation.definition?.valueType;

    return t('ErrorStrategyType', {
      type: t(valueType ? `QuantityValueType.${valueType}` : ('Input' as I18nKey)),
    });
  }

  // Check integer validation based on the computed format
  const effectiveValueType = format.value?.valueType;
  if (effectiveValueType === 'IntegerNumber' && !Number.isInteger(inputValue)) {
    return t('ErrorStrategyType', { type: t('QuantityValueType.IntegerNumber') });
  }

  const tooSmall = minValue !== undefined && inputValue < minValue;
  const tooBig = maxValue !== undefined && inputValue > maxValue;

  const unit = props.evaluation.evaluationUnitAbbreviation ?? '';

  if (tooBig || tooSmall) {
    return t('ValidationValueNotInRange', { minValue, maxValue, unit });
  }

  return undefined;
});

function submit(input: number | undefined) {
  if (input !== undefined && !errorMessage.value) {
    emits('input', input);
  }
}
</script>

<template>
  <NumberField
    v-model="modelValue"
    :label="$t('Value')"
    :suffix="evaluation.evaluationUnitAbbreviation"
    :disabled="disabled"
    :errorMessage="errorMessage"
    :format="format"
    class="mr-2 flex-grow-1"
    @blur="submit(modelValue)"
    @keyup.enter="$event.srcElement.blur()"
  />
</template>
